import { Fragment, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Phone, PhoneCall, PhoneOff, Settings, Search, Check, Smartphone, ShieldCheck, Loader2,
  Delete, RefreshCw, Play, ChevronDown, ChevronUp, LogIn, Upload, ArrowRightLeft,
  Wifi, WifiOff,
} from "lucide-react";
import { Device } from "@twilio/voice-sdk";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useReference } from "@/context/ReferenceContext";
import { PageHeader } from "@/components/CrudModule";
import PowerDialer from "@/components/PowerDialer";
import TelephonyLeadDialer from "@/components/TelephonyLeadDialer";
import RecoveryDialer from "@/components/RecoveryDialer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Telephony() {
  const { user } = useAuth();
  const isAdmin = ["admin", "ops_manager"].includes(user.role);
  const isBpo = user.role === "bpo_closer";
  const [st, setSt] = useState(null);
  const load = () => api.get("/voice/status").then(({ data }) => setSt(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div>
      <PageHeader title="Telephony" subtitle={isBpo
        ? "Auto Dialer — cold calls, uploaded lead lists, and callbacks for Kevin's outbound team."
        : "Connect the calling provider, provision an agency number, and place recorded outbound calls."} />
      {!st ? <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div> : (
        <div className="space-y-5">
          <StatusBar st={st} />
          {(user.role === "agent" || user.role === "bpo_closer" || isAdmin) && <WorkflowHero />}
          {isAdmin && <AdminSetup st={st} reload={load} />}
          {(user.role === "agent" || user.role === "bpo_closer" || isAdmin) && <CallingPanels st={st} isBpo={isBpo} />}
        </div>
      )}
    </div>
  );
}

function StatusBar({ st }) {
  return (
    <div className="brand-tech-surface flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border px-5 py-3.5 text-sm card-elevated">
      <span className={`inline-flex items-center gap-2 font-semibold ${st.connected ? "text-emerald-700" : "text-amber-700"}`}>
        {st.connected ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
        <span className={`h-2 w-2 rounded-full ${st.connected ? "bg-emerald-500 animate-pulse" : "bg-amber-500"}`} />
        {st.connected ? "Provider Connected" : "Provider Not Connected"}
      </span>
      {st.account_sid_masked && (
        <span className="text-muted-foreground font-mono text-xs border-l border-border pl-6">{st.account_sid_masked}</span>
      )}
      <span className="text-muted-foreground border-l border-border pl-6">Agency #: <strong className="text-foreground font-mono">{st.number || "none"}</strong></span>
      <span className="text-muted-foreground">Assigned: <strong className="text-foreground">{st.assigned_to || "unassigned"}</strong></span>
      <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700">
        <ShieldCheck className="h-3.5 w-3.5" /> Recording on by default · state disclosure policy applied
      </span>
    </div>
  );
}

const WORKFLOW_STEPS = [
  { icon: LogIn, title: "1. Log In", detail: "Sign in and open Telephony — Auto Dialer is your primary workspace." },
  { icon: Upload, title: "2. Upload Lead List", detail: "Import a CSV/XLSX of cold leads into a campaign queue." },
  { icon: PhoneCall, title: "3. Make Calls", detail: "Start Calling — the queue auto-advances after each disposition." },
  { icon: ArrowRightLeft, title: "4. 3-Way / Transfer", detail: "Add a person or transfer live, without dropping the customer." },
];

function WorkflowHero() {
  return (
    <div className="rounded-lg border border-border bg-card p-5 card-elevated" data-testid="telephony-workflow-hero">
      <div className="overline mb-3">How This Works</div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {WORKFLOW_STEPS.map((s, i) => (
          <div key={s.title} className="relative flex gap-3 rounded-md border border-border bg-secondary/20 p-3">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gold" style={{ color: "hsl(var(--graphite-deep))" }}>
              <s.icon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold font-heading">{s.title}</div>
              <div className="text-xs text-muted-foreground leading-snug">{s.detail}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminSetup({ st, reload }) {
  const { users } = useReference();
  const setupComplete = st.connected && st.number;
  const [open, setOpen] = useState(!setupComplete);
  const [c, setC] = useState({ account_sid: "", auth_token: "", api_key_sid: "", api_key_secret: "", twiml_app_sid: "" });
  const [busy, setBusy] = useState(false);
  const [area, setArea] = useState("");
  const [found, setFound] = useState([]);
  const [agentId, setAgentId] = useState("");
  const [ownNum, setOwnNum] = useState("");
  const set = (k, v) => setC((s) => ({ ...s, [k]: v }));
  const agents = (users || []).filter((u) => ["agent", "admin", "bpo_closer"].includes(u.role) && u.active !== false);

  const connect = async () => {
    setBusy(true);
    try { await api.post("/voice/connect", c); toast.success("Provider connected"); reload(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const search = async () => {
    setBusy(true);
    try { const { data } = await api.post("/voice/numbers/search", { area_code: area }); setFound(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const buy = async (phone_number) => {
    setBusy(true);
    try { await api.post("/voice/numbers/purchase", { phone_number }); toast.success("Number purchased"); setFound([]); reload(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const importExisting = async () => {
    if (!ownNum.trim()) { toast.error("Enter the number you already own"); return; }
    setBusy(true);
    try { const { data } = await api.post("/voice/numbers/import", { e164: ownNum }); toast.success(`Registered ${data.e164}`); setOwnNum(""); reload(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const assign = async () => {
    try { const { data } = await api.post("/voice/numbers/assign", { agent_id: agentId }); toast.success(`Assigned to ${data.assigned_to}`); reload(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className={`rounded-lg border border-border bg-card ${open ? "p-5" : "px-5 py-3"}`} data-testid="admin-setup-panel">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between text-left" data-testid="admin-setup-toggle">
        <span className="overline flex items-center gap-1.5"><Settings className="h-3.5 w-3.5" /> Owner / Admin Setup
          {setupComplete && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium normal-case tracking-normal text-emerald-700"><Check className="h-3 w-3" /> Configured</span>}
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>
      {open && <div className="mt-3">
      {!st.connected ? (
        <div className="space-y-3 max-w-xl">
          <p className="text-sm text-muted-foreground">Enter your Twilio credentials (from the Twilio Console). Stored encrypted; never shown again.</p>
          <div className="rounded-md border border-[#B28A3D]/30 bg-[#B28A3D]/5 p-3 text-[11px] text-[#5f4a1f] space-y-1">
            <div className="font-semibold text-[#7a5d27]">Paste these exact URLs into your Twilio Console</div>
            <div>TwiML App · Voice Request URL <span className="text-muted-foreground">(POST)</span>: <code className="font-mono">{BACKEND}/api/voice/voice</code></div>
            <div>Call Status Callback <span className="text-muted-foreground">(POST)</span>: <code className="font-mono">{BACKEND}/api/voice/status-callback</code></div>
            <div>Recording Status Callback <span className="text-muted-foreground">(POST)</span>: <code className="font-mono">{BACKEND}/api/voice/recording-callback</code></div>
            <div className="text-muted-foreground pt-1">Use a <strong>Standard</strong> API Key. Never paste secrets into chat or email — only into these fields.</div>
          </div>
          {[["account_sid", "Account SID"], ["auth_token", "Auth Token"], ["api_key_sid", "API Key SID"], ["api_key_secret", "API Key Secret"], ["twiml_app_sid", "TwiML App SID"]].map(([k, l]) => (
            <div key={k}><Label className="text-xs">{l}</Label><Input type={k.includes("token") || k.includes("secret") ? "password" : "text"} value={c[k]} onChange={(e) => set(k, e.target.value)} data-testid={`tw-${k}`} /></div>
          ))}
          <Button onClick={connect} disabled={busy} data-testid="tw-connect">{busy ? "Connecting…" : "Connect Provider"}</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {!st.number ? (
            <div>
              <div className="rounded-md border border-[#315A7D]/25 bg-[#315A7D]/5 p-3 mb-4">
                <Label className="text-xs font-medium text-[#2c5170]">Use an agency number you already own</Label>
                <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">Enter your existing Twilio number (e.g. (954) 516-2206). We'll attach it and point its Voice webhook to the platform.</p>
                <div className="flex items-end gap-2">
                  <Input value={ownNum} onChange={(e) => setOwnNum(e.target.value)} className="w-56" placeholder="(954) 516-2206" data-testid="tw-ownnum" />
                  <Button onClick={importExisting} disabled={busy} data-testid="tw-import"><Check className="mr-1.5 h-4 w-4" /> Register Existing Number</Button>
                </div>
              </div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">— or buy a new number —</div>
              <div className="flex items-end gap-2">
                <div><Label className="text-xs">Area code (optional)</Label><Input value={area} onChange={(e) => setArea(e.target.value)} className="w-28" placeholder="e.g. 954" /></div>
                <Button variant="outline" onClick={search} disabled={busy} data-testid="tw-search"><Search className="mr-1.5 h-4 w-4" /> Search Numbers</Button>
              </div>
              <div className="mt-3 space-y-1.5">
                {found.map((n) => (
                  <div key={n.phone_number} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                    <span className="font-mono">{n.phone_number} <span className="text-muted-foreground text-xs">{n.locality}, {n.region}</span></span>
                    <Button size="sm" onClick={() => buy(n.phone_number)} disabled={busy} data-testid="tw-buy">Buy &amp; Connect</Button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-end gap-2">
              <div><Label className="text-xs">Assign agency number to agent</Label>
                <Select value={agentId} onValueChange={setAgentId}>
                  <SelectTrigger className="w-64" data-testid="tw-agent"><SelectValue placeholder="Select agent" /></SelectTrigger>
                  <SelectContent>{agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={assign} disabled={!agentId} data-testid="tw-assign"><Check className="mr-1.5 h-4 w-4" /> Assign</Button>
            </div>
          )}
        </div>
      )}
      </div>}
    </div>
  );
}

const CALLING_TABS = [
  { key: "leads", label: "Auto Dialer", testid: "calling-tab-leads" },
  { key: "dialer", label: "Client Follow-Up", testid: "calling-tab-dialer" },
  { key: "softphone", label: "Softphone", testid: "calling-tab-softphone" },
  { key: "recovery", label: "Recovery", testid: "calling-tab-recovery" },
];

function CallingPanels({ st, isBpo }) {
  const [tab, setTab] = useState("leads");
  const providerReady = st.connected && st.number;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap rounded-lg border border-border bg-card p-1.5 gap-1" role="tablist">
        {CALLING_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            data-testid={t.testid}
            className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
              tab === t.key
                ? "brand-action shadow-sm"
                : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
            }`}
          >
            {t.label}
            {t.key === "leads" && isBpo && (
              <span className="ml-1.5 align-middle text-[9px] font-bold uppercase tracking-wider opacity-70">Primary</span>
            )}
          </button>
        ))}
      </div>
      {!providerReady ? (
        <div className="rounded-lg border border-border bg-card p-5 text-sm text-muted-foreground">
          Calling becomes available once the provider is connected and an agency number is assigned.
        </div>
      ) : tab === "leads" ? <TelephonyLeadDialer />
        : tab === "dialer" ? <PowerDialer />
        : tab === "recovery" ? <RecoveryDialer />
        : <Softphone st={st} />}
    </div>
  );
}

function Softphone({ st }) {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const LS_KEY = `foa_active_call_${user.id}`;
  const [leads, setLeads] = useState([]);
  const [leadId, setLeadId] = useState("");
  const [to, setTo] = useState("");
  const [cell, setCell] = useState("");
  const [calling, setCalling] = useState(false);
  const [callLogId, setCallLogId] = useState(() => localStorage.getItem(`foa_active_call_${user.id}`) || null);
  const [disp, setDisp] = useState({ disposition: "", notes: "", next_action: "", next_date: "" });
  const [history, setHistory] = useState([]);
  const [recCallId, setRecCallId] = useState(null);
  const [recUrl, setRecUrl] = useState(null);
  const [recLoading, setRecLoading] = useState(null);
  const [recError, setRecError] = useState(null);
  const deviceRef = useRef(null);
  const connRef = useRef(null);
  const audioUrlRef = useRef(null);
  const audioElRef = useRef(null);

  const refreshHistory = () =>
    api.get("/voice/calls").then(({ data }) => setHistory((data || []).slice(0, 12))).catch(() => {});
  const refreshHistorySoon = () => { refreshHistory(); setTimeout(refreshHistory, 1800); setTimeout(refreshHistory, 5000); };

  // Destroying the Device is what releases Chrome's mic light. Keep callLogId for wrap-up.
  const cleanupDevice = () => {
    try { connRef.current?.disconnect(); } catch (e) {}
    try { deviceRef.current?.destroy(); } catch (e) {}
    connRef.current = null;
    deviceRef.current = null;
    setCalling(false);
  };

  useEffect(() => {
    let alive = true;
    api.get("/leads").then(({ data }) => {
      if (!alive) return;
      setLeads(data);
      const requestedLead = searchParams.get("lead");
      const requestedPhone = searchParams.get("to");
      if (requestedPhone) setTo(requestedPhone);
      if (requestedLead) {
        const linked = data.find((x) => x.id === requestedLead);
        if (linked) {
          setLeadId(linked.id);
          setTo(linked.phone || requestedPhone || "");
          toast.info(`Ready to follow up with ${linked.client_name}`);
        }
      } else if (requestedPhone && searchParams.get("followup")) {
        toast.info("Follow Up Now loaded this client's phone automatically.");
      }
    }).catch(() => {});
    refreshHistory();
    return () => { alive = false; };
  }, [searchParams]);

  // Cleanup on unmount (release device/mic; revoke recording URL).
  useEffect(() => () => {
    try { connRef.current?.disconnect(); } catch (e) {}
    try { deviceRef.current?.destroy(); } catch (e) {}
    if (audioUrlRef.current) { try { URL.revokeObjectURL(audioUrlRef.current); } catch (e) {} }
  }, []);

  // After the inline player renders, attempt playback; if autoplay is blocked keep controls.
  useEffect(() => {
    if (recUrl && audioElRef.current) {
      const p = audioElRef.current.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => toast.info("Recording loaded — press play in the audio player."));
      }
    }
  }, [recUrl]);

  const pickLead = (id) => {
    setLeadId(id);
    const l = leads.find((x) => x.id === id);
    setTo(l?.phone || "");
  };

  const DIAL_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];
  const pressKey = (k) => setTo((v) => (v || "") + k);
  const backspace = () => setTo((v) => (v || "").slice(0, -1));
  const clearNum = () => setTo("");

  const persistCall = (id) => { setCallLogId(id); if (id) localStorage.setItem(LS_KEY, id); else localStorage.removeItem(LS_KEY); };

  const startBrowserCall = async () => {
    if (!to) { toast.error("Enter a phone number to dial"); return; }
    try {
      const { data: c } = await api.post("/voice/calls", { lead_id: leadId, to });
      persistCall(c.call_log_id);
      if (c.disclosure) toast.info("This client's state requires a recording disclosure — it will be played.");
      const { data: t } = await api.post("/voice/token");
      const device = new Device(t.token, { logLevel: 1 });
      deviceRef.current = device;
      device.on("error", () => { cleanupDevice(); refreshHistorySoon(); });
      const conn = await device.connect({ params: { To: to, CallLogId: c.call_log_id } });
      connRef.current = conn;
      setCalling(true);
      ["disconnect", "cancel", "reject", "error"].forEach((ev) =>
        conn.on(ev, () => { cleanupDevice(); refreshHistorySoon(); }));
    } catch (err) {
      cleanupDevice();
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Call could not start");
    }
  };

  const dialMyCell = async () => {
    if (!leadId || !to || !cell) { toast.error("Select a lead, client number, and your cell"); return; }
    try {
      const { data } = await api.post("/voice/fallback", { lead_id: leadId, to, agent_cell: cell });
      persistCall(data.call_log_id);
      toast.success("Calling your cell — answer to connect to the client");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const hangup = () => { cleanupDevice(); refreshHistorySoon(); };

  const playRecording = async (call) => {
    setRecError(null);
    setRecLoading(call.id);
    try {
      const res = await api.get(`/voice/calls/${call.id}/recording`, { responseType: "blob" });
      const blob = res.data;
      const ct = (res.headers?.["content-type"] || blob?.type || "").toLowerCase();
      // Empty blob or JSON/text error payload means there is no playable audio.
      const bad = !blob || blob.size === 0 || ct.includes("json") || ct.includes("text/");
      if (bad) {
        let msg = "Recording is not available yet. Please refresh in a moment.";
        try {
          if (blob && (ct.includes("json") || ct.includes("text/"))) {
            const txt = await blob.text();
            try { msg = JSON.parse(txt).detail || msg; } catch (e) { if (txt) msg = txt; }
          }
        } catch (e) {}
        setRecError({ id: call.id, msg });
        toast.error(msg);
        return;
      }
      if (audioUrlRef.current) { try { URL.revokeObjectURL(audioUrlRef.current); } catch (e) {} }
      const url = URL.createObjectURL(blob);
      audioUrlRef.current = url;
      setRecUrl(url);
      setRecCallId(call.id);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Recording could not be loaded.";
      setRecError({ id: call.id, msg });
      toast.error(msg);
    } finally {
      setRecLoading(null);
    }
  };

  const saveDisp = async () => {
    if (!disp.disposition || !disp.next_action) { toast.error("Disposition and next action are required"); return; }
    try {
      await api.post(`/voice/calls/${callLogId}/disposition`, disp);
      const sourceFollowup = searchParams.get("followup");
      if (sourceFollowup) {
        const completed = ["Connected - Interested", "Connected - Not Interested", "Wrong Number", "Do Not Call"].includes(disp.disposition);
        await api.put(`/followups/${sourceFollowup}`, {
          status_state: completed ? "completed" : "rescheduled",
          last_contact_date: new Date().toISOString().slice(0, 10),
          last_contact_result: disp.disposition,
          next_action: disp.next_action,
          due_date: disp.next_date || undefined,
        }).catch(() => {});
      }
      toast.success(sourceFollowup ? "Call logged and follow-up updated" : "Call logged");
      persistCall(null);
      setDisp({ disposition: "", notes: "", next_action: "", next_date: "" });
      refreshHistorySoon();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const myLeads = leads.filter((l) => !["Closed Lost", "Client Declined"].includes(l.stage));
  const notReady = !st.connected || !st.number;

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-border bg-card p-5">
        <div className="overline mb-3 flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> Softphone</div>
        {notReady ? (
          <p className="text-sm text-muted-foreground">Calling becomes available once the provider is connected and an agency number is assigned.</p>
        ) : (callLogId && !calling) ? (
          <div className="max-w-lg space-y-3" data-testid="disposition-form">
            <p className="text-sm font-medium">Wrap up the call — disposition & next action are required.</p>
            <div><Label className="text-xs">Disposition *</Label>
              <Select value={disp.disposition} onValueChange={(v) => setDisp({ ...disp, disposition: v })}>
                <SelectTrigger data-testid="disp-select"><SelectValue placeholder="Outcome" /></SelectTrigger>
                <SelectContent>{["Connected - Interested", "Connected - Not Interested", "Left Voicemail", "No Answer", "Wrong Number", "Callback Scheduled", "Do Not Call"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Next Action *</Label><Input value={disp.next_action} onChange={(e) => setDisp({ ...disp, next_action: e.target.value })} data-testid="disp-next" /></div>
              <div><Label className="text-xs">Follow-Up Date</Label><Input type="date" value={disp.next_date} onChange={(e) => setDisp({ ...disp, next_date: e.target.value })} /></div>
            </div>
            <div><Label className="text-xs">Notes</Label><Textarea value={disp.notes} onChange={(e) => setDisp({ ...disp, notes: e.target.value })} /></div>
            <Button onClick={saveDisp} data-testid="disp-save">Save Call Log</Button>
          </div>
        ) : (
          <div className="max-w-lg space-y-3">
            {searchParams.get("followup") && to && (
              <div className="rounded-md border border-[#B28A3D]/30 bg-[#B28A3D]/10 px-3 py-2 text-xs text-[#5f4a1f]" data-testid="follow-up-prefill">
                Follow Up Now loaded this client automatically. Place the call, then save the disposition and next action here.
              </div>
            )}
            <div><Label className="text-xs">Lead (optional)</Label>
              <Select value={leadId} onValueChange={pickLead}>
                <SelectTrigger data-testid="sp-lead"><SelectValue placeholder="Optional — select a saved lead" /></SelectTrigger>
                <SelectContent>{myLeads.map((l) => <SelectItem key={l.id} value={l.id}>{l.client_name} · {l.display_id} · {l.state || "—"}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Phone Number</Label>
              <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Type a number and dial" data-testid="sp-to" />
              <p className="text-xs text-muted-foreground mt-1">Quick Dial does not require a saved lead.</p>
            </div>

            {/* Dial pad */}
            <div className="max-w-[240px]" data-testid="dial-pad">
              <div className="grid grid-cols-3 gap-2">
                {DIAL_KEYS.map((k) => (
                  <Button key={k} type="button" variant="outline" className="h-11 text-base font-medium"
                    onClick={() => pressKey(k)} data-testid={`dial-${k === "*" ? "star" : k === "#" ? "hash" : k}`} disabled={calling}>
                    {k}
                  </Button>
                ))}
              </div>
              <div className="mt-2 flex gap-2">
                <Button type="button" variant="ghost" className="flex-1" onClick={clearNum} data-testid="dial-clear" disabled={calling}>Clear</Button>
                <Button type="button" variant="ghost" className="flex-1" onClick={backspace} data-testid="dial-backspace" disabled={calling}><Delete className="h-4 w-4" /></Button>
              </div>
            </div>

            {!calling ? (
              <div className="flex gap-2">
                <Button onClick={startBrowserCall} data-testid="sp-call"><PhoneCall className="mr-1.5 h-4 w-4" /> Call Number</Button>
              </div>
            ) : (
              <div className="space-y-2" data-testid="call-in-progress">
                <div className="inline-flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" /> Call in progress — {to}
                </div>
                <div><Button variant="destructive" onClick={hangup} data-testid="sp-hangup"><PhoneOff className="mr-1.5 h-4 w-4" /> Hang Up</Button></div>
              </div>
            )}

            <div className="border-t border-border pt-3">
              <Label className="text-xs flex items-center gap-1"><Smartphone className="h-3.5 w-3.5" /> Call Through My Phone</Label>
              <div className="flex gap-2 mt-1">
                <Input value={cell} onChange={(e) => setCell(e.target.value)} placeholder="Your cell number" data-testid="sp-cell" />
                <Button variant="outline" onClick={dialMyCell} data-testid="sp-fallback">Ring My Cell</Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recent calls & recordings */}
      {!notReady && (
        <div className="rounded-lg border border-border bg-card p-5" data-testid="recent-calls">
          <div className="mb-3 flex items-center justify-between">
            <div className="overline flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> Recent Calls</div>
            <Button variant="outline" size="sm" onClick={refreshHistory} data-testid="recent-refresh"><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh</Button>
          </div>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground">No calls yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="py-1.5 pr-3">Number</th><th className="py-1.5 pr-3">Call ID</th>
                    <th className="py-1.5 pr-3">When</th><th className="py-1.5 pr-3">Dir</th>
                    <th className="py-1.5 pr-3">Status</th><th className="py-1.5 pr-3">Dur</th>
                    <th className="py-1.5 pr-3">Disposition</th><th className="py-1.5 pr-3">Recording</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((c) => (
                    <Fragment key={c.id}>
                    <tr className="border-b border-border/50" data-testid={`call-row-${c.id}`}>
                      <td className="py-1.5 pr-3 font-mono">{c.to_number || c.from_number || "—"}</td>
                      <td className="py-1.5 pr-3 font-mono">{c.display_id}</td>
                      <td className="py-1.5 pr-3">{(c.initiated_at || c.started_at) ? new Date(c.initiated_at || c.started_at).toLocaleString() : "—"}</td>
                      <td className="py-1.5 pr-3">{c.direction || "—"}</td>
                      <td className="py-1.5 pr-3">{c.status || "—"}</td>
                      <td className="py-1.5 pr-3">{c.duration_sec != null ? `${c.duration_sec}s` : "—"}</td>
                      <td className="py-1.5 pr-3">{c.disposition || "—"}</td>
                      <td className="py-1.5 pr-3">
                        {c.recording_status === "ready" ? (
                          <Button variant="outline" size="sm" onClick={() => playRecording(c)} disabled={recLoading === c.id} data-testid={`play-recording-${c.id}`}>
                            {recLoading === c.id ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Play className="mr-1 h-3 w-3" />}
                            {recLoading === c.id ? "Loading" : "Play"}
                          </Button>
                        ) : (c.recording_status && c.recording_status !== "none" ? c.recording_status : "—")}
                      </td>
                    </tr>
                    {(recError?.id === c.id || (recCallId === c.id && recUrl)) && (
                      <tr data-testid={`recording-row-${c.id}`}>
                        <td colSpan={8} className="pb-3 pr-3">
                          {recError?.id === c.id ? (
                            <p className="text-xs text-destructive" data-testid={`recording-error-${c.id}`}>{recError.msg}</p>
                          ) : (
                            <audio ref={audioElRef} controls preload="auto" src={recUrl} className="w-full"
                              data-testid="recording-audio"
                              onError={() => setRecError({ id: c.id, msg: "Playback failed in the browser for this recording." })} />
                          )}
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
